import React, {useState, useEffect} from 'react'
import _ from "lodash"
import {connect} from "react-redux"
import {useRoute} from "react-router5"
import {httpClient} from "../../funcs"
import {useMediaQuery} from "@mui/material"
import {mobileMaxWidthMediaQuery} from "../../methods/utils"
import {notifyAboutError, notifyUser} from "../../methods/notifyUser"
import { AdditionalServiceType, isAdditionalService } from "../../util/AdditionalServiceType"
import { getFirstPurchasePromoCode } from "../../util/promoCode"
import {useLocalStorage} from "../../methods/useLocalStorage"
import { TariffUpgradeType } from "../../methods/TariffUpgradeType"
import TariffCards from "./TariffCards"
import AdditionalServices from "./AdditionalServices"
import Cart from "./Cart"
import TabSelectorV2 from "./TabSelectorV2"
import BillsList from "./BillsList"
import CustomQuantitySlider from "./CustomQuantitySlider"
import FloatingCartButton from "../../components/elements/FloatingCartButton"
import ConfigureTemplateAfterPaymentPopup from "./ConfigureTemplateAfterPaymentPopup"
import RequestBillPopupV2 from "../Profile/RequestBillPopupV2"
import SubscriptionPopup from "../Profile/SubscriptionPopup"
import "pages/Styles/ProSubscription.sass"
import "pages/Styles/TariffsV2Page.sass"
import "pages/Styles/CustomQuantitySlider.sass"
import "pages/Styles/UIKit_v2.sass"
import {PageLoader} from "../Contracts/ContractList/PageLoader"
import { 
    extractPricePoints, 
    extractPricePointsWithoutDiscount,
    extractUpgradePricePoints, 
    calculatePricePerDocument, 
    calculateUpgradePrices,
    calculateDiscountPercent
} from "../../util/priceCalculation"
import { buildCustomPackageCartItem } from "../../methods/onboardingPayOffer"

// Функция для получения максимальной скидки среди тарифов
function getMaxDiscount(tariffs: any[]): number {
    // Находим максимальную скидку среди всех тарифов
    if (tariffs.length === 0) {
        return 0
    }
    return Math.max(...tariffs.map(tariff => tariff.discount || 0))
}

function getMinDiscount(tariffs: any[]): number {
    // Находим минимальную скидку среди всех тарифов (исключая 0)
    if (tariffs.length === 0) {
        return 0
    }
    const discounts = tariffs
        .map(tariff => tariff.discount || 0)
        .filter(discount => discount > 0)
    if (discounts.length === 0) {
        return 0
    }
    return Math.min(...discounts)
}
import MyTariffV2 from './MyTariffV2'
import PaymentStatus from "../../components/payment/PaymentStatus"
import { ErrorPageView } from "../../components/ErrorPageView"
import TransactionHistory from './TransactionHistory'

let TariffsV2Page: any = function TariffsV2Page(props: any) {
    const $router = useRoute()
    const mobile = useMediaQuery(mobileMaxWidthMediaQuery)
    
    // Инициализация localStorage для корзины и активной вкладки
    const [getCartFromStorage, setCartToStorage, removeCartFromStorage] = useLocalStorage('tariffs_cart')
    const [getActiveTabFromStorage, setActiveTabToStorage, removeActiveTabFromStorage] = useLocalStorage('tariffs_active_tab')
    
    const [tariffsData, setTariffsData] = useState<any>({})
    const [cart, setCart] = useState<any[]>(() => getCartFromStorage() || [])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<any>(null)
    const [activeTab, setActiveTab] = useState(() => getActiveTabFromStorage() || 'Тарифы')
    const [paymentNotification, setPaymentNotification] = useState<boolean>(false)
    const [showTemplateConfigPopup, setShowTemplateConfigPopup] = useState(false)
    const [pendingPaymentMethod, setPendingPaymentMethod] = useState<'card' | 'invoice' | 'sbp' | null>(null)
    const [showBillPopup, setShowBillPopup] = useState(false)
    const [billItems, setBillItems] = useState<any[]>([])
    const [subscriptionPopupTariff, setSubscriptionPopupTariff] = useState<any>(null)
    const promoCodes = getFirstPurchasePromoCode(props.$store?.userSession) || {}

    useEffect(() => {
        loadTariffs()
    }, [])

    // WhatsApp-апгрейд недоступен для юрисдикции РФ — убираем из корзины (в т.ч. из localStorage)
    useEffect(() => {
        const jurisdiction = props.$store?.userSession?.jurisdiction
        if (TariffUpgradeType.isSigningWhatsappAvailable(jurisdiction)) {
            return
        }
        setCart(prevCart => {
            let changed = false
            const next = prevCart.map(item => {
                if (item?.type === 'upgrade' && item._id === TariffUpgradeType.SigningWhatsapp) {
                    changed = true
                    return null
                }
                if (item?.type === 'package_v2' && Array.isArray(item.tariff_upgrades)) {
                    const filtered = item.tariff_upgrades.filter(
                        (id: string) => id !== TariffUpgradeType.SigningWhatsapp,
                    )
                    if (filtered.length !== item.tariff_upgrades.length) {
                        changed = true
                        return { ...item, tariff_upgrades: filtered }
                    }
                }
                return item
            }).filter(Boolean)
            return changed ? next : prevCart
        })
    }, [props.$store?.userSession?.jurisdiction])

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const success = $router.route.params.success || urlParams.get('success')
        const transactionId = $router.route.params.transactionId || urlParams.get('transactionId')
        if (success && transactionId) {
            setPaymentNotification(true)
        }
    }, [$router])

    // Обработка добавления тарифа в корзину через URL параметр
    useEffect(() => {
        // В router5 query параметры доступны через route.params, если они объявлены в пути
        // Также проверяем напрямую из URL на случай, если router5 не обработал параметр
        const urlParams = new URLSearchParams(window.location.search)
        const addToCartType = $router.route.params.add_to_cart || urlParams.get('add_to_cart')
        const addToCartTariffId = $router.route.params.add_to_cart_tariff_id || urlParams.get('add_to_cart_tariff_id')
        const customQuantityRaw = $router.route.params.custom_quantity || urlParams.get('custom_quantity')
        const templateId = $router.route.params.template_id || urlParams.get('template_id')
        const startAiConfigurationParam = $router.route.params.start_ai_configuration || urlParams.get('start_ai_configuration')
        
        // Отладка: логируем все параметры
        if (addToCartType || urlParams.get('add_to_cart')) {
            console.log('TariffsV2Page - Route params:', $router.route.params)
            console.log('TariffsV2Page - URL params:', urlParams.toString())
            console.log('TariffsV2Page - addToCartType:', addToCartType)
        }
        
        if (addToCartType === 'package_v2' && tariffsData.tariffs?.length) {
            const customQuantity = Number(customQuantityRaw)
            if (customQuantity > 0) {
                const customItem = buildCustomPackageCartItem(tariffsData.tariffs, customQuantity)
                setCart(prevCart => [
                    ...prevCart.filter((item) => item.type !== 'package_v2' && item.type !== 'pro_subscription'),
                    customItem,
                ])
                notifyUser(props, 'Тариф добавлен в корзину', `На выбор · ${customQuantity} договоров`)
            } else if (addToCartTariffId) {
                const tariff = tariffsData.tariffs.find(
                    (item: any) => String(item._id) === String(addToCartTariffId),
                )
                if (tariff) {
                    setCart(prevCart => [
                        ...prevCart.filter((item) => item.type !== 'package_v2' && item.type !== 'pro_subscription'),
                        { ...tariff, type: 'package_v2', discount: tariff.discount || 0 },
                    ])
                    notifyUser(props, 'Тариф добавлен в корзину', tariff.name || 'Пакет')
                }
            }
            $router.router.navigate($router.route.name, {}, { reload: false })
            return
        }

        if (addToCartType && isAdditionalService(addToCartType) && tariffsData[addToCartType]) {
            const list = tariffsData[addToCartType]
            const tariff = addToCartTariffId
                ? list.find((t: any) => String(t._id) === String(addToCartTariffId))
                : list[0]
            if (!tariff) return

            const shouldStartAi = startAiConfigurationParam === null || startAiConfigurationParam === undefined
                ? false
                : !(startAiConfigurationParam === 'false' || startAiConfigurationParam === '0')
            const withTemplate =
                addToCartType === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION && templateId
                    ? { template_id: templateId, start_ai_configuration: shouldStartAi }
                    : addToCartType === AdditionalServiceType.TEMPLATE_CONFIGURATION && templateId
                        ? { template_id: templateId }
                        : {}

            if (
                (addToCartType === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION
                    || addToCartType === AdditionalServiceType.TEMPLATE_CONFIGURATION)
                && templateId
            ) {
                setCart(prevCart => {
                    const withoutSameType = prevCart.filter(item => item.type !== addToCartType)
                    return [
                        ...withoutSameType,
                        {
                            ...tariff,
                            type: addToCartType,
                            quantity: 1,
                            ...withTemplate
                        }
                    ]
                })
            } else if (!cart.some(item => item.type === addToCartType && item._id === tariff._id)) {
                addAdditionalServiceToCart({
                    ...tariff,
                    type: addToCartType,
                    quantity: 1,
                    ...withTemplate
                })
            }

            // Показываем уведомление
            const serviceName = addToCartType === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION
                ? 'Настройка шаблона ИИ'
                : addToCartType === AdditionalServiceType.TEMPLATE_CONFIGURATION
                    ? 'Настройка шаблона специалистом'
                    : tariff.name || 'Услуга'
            notifyUser(props, 'Тариф добавлен в корзину', `${serviceName} добавлен в корзину`)
            // Убираем параметр из URL через router (используем текущий роут)
            $router.router.navigate($router.route.name, {}, { reload: false })
        }
    }, [tariffsData, $router, cart])

    // Сохраняем корзину в localStorage при каждом изменении
    useEffect(() => {
        if (cart.length > 0) {
            setCartToStorage(cart)
        } else {
            removeCartFromStorage()
        }
    }, [cart, setCartToStorage, removeCartFromStorage])

    // Сохраняем активную вкладку в localStorage при изменении
    useEffect(() => {
        setActiveTabToStorage(activeTab)
    }, [activeTab, setActiveTabToStorage])

    async function loadTariffs() {
        try {
            setLoading(true)
            setError(null)
            const {data: tariffsData} = await httpClient.get("/tariffs-v2")
            setTariffsData(tariffsData)
        } catch (error) {
            console.error("Ошибка при загрузке тарифов:", error)
            setError(error)
            notifyAboutError(props, error)
        } finally {
            setLoading(false)
        }
    }

    function addTariffToCart(tariff: any) {
        console.log('Adding tariff to cart:', tariff)
        setCart(prevCart => {
            // Проверяем, есть ли уже тариф в корзине
            const existingTariffIndex = prevCart.findIndex(cartItem => 
                cartItem.type === 'package_v2' && cartItem._id === tariff._id
            )
            
            // Если тариф уже есть в корзине, удаляем его (toggle off)
            if (existingTariffIndex >= 0) {
                const newCart = prevCart.filter((_, index) => index !== existingTariffIndex)
                console.log('Removed tariff from cart:', newCart)
                return newCart
            }
            
            // Удаляем все существующие тарифы и Pro из корзины
            const filteredCart = prevCart.filter(cartItem =>
                cartItem.type !== 'package_v2' && cartItem.type !== 'pro_subscription'
            )
            
            // Если это индивидуальный тариф, добавляем его с базовыми параметрами
            if (tariff._id === 'custom') {
                // Для индивидуального тарифа используем базовый тариф как основу
                const baseTariff = tariffsData.tariffs?.[0]
                if (baseTariff) {
                    // Находим тариф с минимальной ценой (оригинальной, без скидки)
                    const tariffs = tariffsData.tariffs || []
                    const minPriceTariff = tariffs.length > 0 ? tariffs.reduce((min: any, t: any) => {
                        return t.price < min.price ? t : min
                    }) : baseTariff
                    
                    // При первом добавлении используем минимальную скидку и оригинальную цену минимального тарифа
                    // Реальная скидка будет вычислена при выборе количества через слайдер
                    const minDiscount = getMinDiscount(tariffs)
                    
                    const customTariff = {
                        ...tariff,
                        prepaid_documents_count: baseTariff.prepaid_documents_count,
                        price: minPriceTariff.price, // Используем оригинальную цену минимального тарифа
                        discount: minDiscount, // Используем минимальную скидку при первом добавлении
                        upgrade_additional_price: baseTariff.upgrade_additional_price,
                        isMinDiscount: true // Флаг, что это минимальная скидка (от -X%)
                    }
                    const newCart = [...filteredCart, customTariff]
                    console.log('Added custom tariff to cart:', newCart)
                    return newCart
                }
            }
            
            // Добавляем новый тариф (убеждаемся, что скидка сохраняется)
            const tariffWithDiscount = {
                ...tariff,
                discount: tariff.discount || 0 // Явно сохраняем скидку
            }
            const newCart = [...filteredCart, tariffWithDiscount]
            console.log('Added tariff to cart:', newCart)
            return newCart
        })
    }

    function addUpgradeToCart(upgrade: any) {
        setCart(prevCart => {
            // Находим тариф в корзине
            const tariffIndex = prevCart.findIndex(item => item.type === 'package_v2')
            
            if (tariffIndex >= 0) {
                const tariff = prevCart[tariffIndex]
                const currentUpgrades = tariff.tariff_upgrades || []
                
                // Проверяем, есть ли уже такой апгрейд
                const upgradeExists = currentUpgrades.includes(upgrade._id)
                
                let newUpgrades
                if (upgradeExists) {
                    // Если апгрейд есть, удаляем его
                    newUpgrades = currentUpgrades.filter((id: string) => id !== upgrade._id)
                } else {
                    // Если апгрейда нет, добавляем его
                    newUpgrades = [...currentUpgrades, upgrade._id]
                }
                
                // Обновляем тариф с новым массивом апгрейдов
                const updatedCart = [...prevCart]
                updatedCart[tariffIndex] = {
                    ...tariff,
                    tariff_upgrades: newUpgrades
                }
                
                console.log('Updated tariff upgrades:', updatedCart[tariffIndex])
                return updatedCart
            }
            
            // Если тарифа нет в корзине, ничего не делаем
            console.log('No tariff in cart, cannot add upgrade')
            return prevCart
        })
    }

    function addProSubscriptionToCart(tariff: any) {
        setCart(prevCart => {
            const existing = prevCart.find(item =>
                item.type === 'pro_subscription' && item._id === tariff._id
            )
            if (existing) {
                return []
            }
            return [{
                ...tariff,
                type: 'pro_subscription',
                quantity: 1,
                discount: tariff.discount || 0,
            }]
        })
    }

    function addAdditionalServiceToCart(service: any) {
        setCart(prevCart => {
            const shouldStartAi = service.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION
                ? (service.start_ai_configuration ?? false)
                : undefined
            const serviceForCart = service.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION
                ? { ...service, start_ai_configuration: shouldStartAi }
                : service
            const existingIndex = prevCart.findIndex(cartItem => 
                cartItem.type === serviceForCart.type && 
                cartItem._id === serviceForCart._id
            )
            
            if (existingIndex >= 0) {
                // Если услуга уже есть в корзине, удаляем её
                const newCart = prevCart.filter((_, index) => index !== existingIndex)
                console.log('Removed service from cart:', newCart)
                return newCart
            } else {
                // Если услуги нет в корзине, добавляем её
                const newCart = [...prevCart, serviceForCart]
                console.log('Added service to cart:', newCart)
                return newCart
            }
        })
    }

    function updateCartItemQuantity(itemId: string, itemType: string, quantity: number) {
        setCart(prevCart => 
            prevCart.map(item => 
                item._id === itemId && item.type === itemType 
                    ? { ...item, quantity }
                    : item
            )
        )
    }

    function clearCart() {
        setCart([])
    }

    function removeCartItem(itemId: string, itemType: string) {
        setCart(prevCart =>
            prevCart.filter(item => !(item._id === itemId && item.type === itemType))
        )
    }

    /** Убрать апгрейд проверки подписанта из выбранного пакета (не удаляя сам пакет). */
    function removePackageUpgrade(upgradeId: string) {
        setCart(prevCart => {
            const tariffIndex = prevCart.findIndex(item => item.type === 'package_v2')
            if (tariffIndex < 0) {
                return prevCart
            }
            const tariff = prevCart[tariffIndex]
            const currentUpgrades = tariff.tariff_upgrades || []
            if (!currentUpgrades.includes(upgradeId)) {
                return prevCart
            }
            const newUpgrades = currentUpgrades.filter((id: string) => id !== upgradeId)
            const updatedCart = [...prevCart]
            updatedCart[tariffIndex] = {
                ...tariff,
                tariff_upgrades: newUpgrades
            }
            return updatedCart
        })
    }

    function handleBillCreated() {

    }

    function handlePaymentProofUploaded(items?: any[]) {
        if (items && items.length > 0) {
            redirectToTemplateAfterPayment(items)
        }
        // Очищаем корзину после загрузки платежного поручения
        clearCart()
    }

    function handleSuccessfulPayment() {
        const items = cart.length > 0 ? [...cart] : []
        const hadPro = items.some(item => item.type === 'pro_subscription')
        clearCart()
        removeCartFromStorage()
        if (hadPro) {
            loadTariffs()
            props.$commitToStore({
                notification: {
                    title: 'Подписка Pro активирована',
                    content: 'Теперь можно настроить оформление страницы подписания для клиентов.',
                },
            })
            $router.router.navigate('contract-branding', {}, { reload: false })
            return
        }
        if (items.length > 0) {
            redirectToTemplateAfterPayment(items)
        }
    }

    async function startPayment(method: 'card' | 'invoice' | 'sbp', items: any[]) {
        try {
            if (method === 'card' || method === 'sbp') {
                const { data: result } = await httpClient.post('/buy-cart', {
                    cart: items,
                    payment_method: method === 'sbp' ? 'sbp' : undefined
                })

                const paymentUrl = result.PaymentURL ?? result.paymentUrl
                if (paymentUrl) {
                    window.location.href = paymentUrl
                } else {
                    notifyAboutError(props, new Error('Не удалось создать платеж'))
                }
            } else {
                setBillItems(items)
                setShowBillPopup(true)
            }
        } catch (error) {
            notifyAboutError(props, error)
        }
    }

    function cartNeedsTemplateSelectionBeforePayment(): boolean {
        return cart.some(item =>
            (item.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION
                || item.type === AdditionalServiceType.TEMPLATE_CONFIGURATION)
            && !item.template_id
        )
    }

    function getTemplateConfigPopupMode(): 'ai' | 'manual' {
        if (cart.some(
            item => item.type === AdditionalServiceType.TEMPLATE_CONFIGURATION && !item.template_id
        )) {
            return 'manual'
        }
        return 'ai'
    }

    function cartHasProSubscription(): boolean {
        return cart.some(item => item.type === 'pro_subscription')
    }

    function cartNeedsSubscriptionConsent(method: 'card' | 'invoice' | 'sbp'): boolean {
        return (method === 'card' || method === 'sbp') && cartHasProSubscription()
    }

    async function handlePaymentRequest(method: 'card' | 'invoice' | 'sbp') {
        if (cartNeedsTemplateSelectionBeforePayment()) {
            setPendingPaymentMethod(method)
            setShowTemplateConfigPopup(true)
            return true
        }

        if (cartNeedsSubscriptionConsent(method)) {
            const proItem = cart.find(item => item.type === 'pro_subscription')
            if (proItem) {
                setSubscriptionPopupTariff(proItem)
                setPendingPaymentMethod(method)
                return true
            }
        }

        return false
    }

    function handleSubscriptionConfirmed() {
        const method = pendingPaymentMethod
        setSubscriptionPopupTariff(null)
        setPendingPaymentMethod(null)
        if (method) {
            void startPayment(method, cart)
        }
    }

    async function handleTemplateSelected(templateId: string) {
        setShowTemplateConfigPopup(false)

        const updatedCart = cart.map(item => {
            if (item.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION) {
                return {
                    ...item,
                    template_id: templateId,
                    start_ai_configuration: false
                }
            }
            if (item.type === AdditionalServiceType.TEMPLATE_CONFIGURATION) {
                return {
                    ...item,
                    template_id: templateId
                }
            }
            return item
        })

        setCart(updatedCart)

        if (pendingPaymentMethod) {
            const method = pendingPaymentMethod
            setPendingPaymentMethod(null)
            await startPayment(method, updatedCart)
        }
    }

    function redirectToTemplateAfterPayment(items: any[]) {
        const manualItem = items.find(item =>
            item.type === AdditionalServiceType.TEMPLATE_CONFIGURATION && item.template_id
        )
        if (manualItem?.template_id) {
            props.$commitToStore({
                notification: {
                    title: 'Оплата получена',
                    content:
                        'Спасибо за оплату! В ближайшее время с вами свяжется менеджер для настройки шаблона.'
                }
            })
            $router.router.navigate(
                'new-contract',
                {
                    act: 'new',
                    templateId: manualItem.template_id
                },
                { reload: false }
            )
            return
        }

        const aiItem = items.find(item =>
            item.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION && item.template_id
        )
        if (!aiItem?.template_id) return

        $router.router.navigate(
            'new-contract',
            {
                act: 'new',
                templateId: aiItem.template_id,
                open_ai_chat: '1',
                ai_chat_greeting: '1'
            },
            { reload: false }
        )
    }

    function isCustomTariffSelected() {
        return cart.some(item => item.type === 'package_v2' && item._id === 'custom')
    }

    function updateCustomTariffQuantity(quantity: number) {
        setCart(prevCart => {
            const tariffIndex = prevCart.findIndex(item => item.type === 'package_v2' && item._id === 'custom')
            
            if (tariffIndex >= 0) {
                const tariff = prevCart[tariffIndex]
                const baseTariff = tariffsData.tariffs?.[0]
                
                if (baseTariff) {
                    // Извлекаем контрольные точки цен БЕЗ скидки для расчета оригинальной цены
                    const pricePointsWithoutDiscount = extractPricePointsWithoutDiscount(tariffsData.tariffs || [])
                    // Извлекаем контрольные точки цен СО СКИДКАМИ для расчета цены со скидкой
                    const pricePointsWithDiscount = extractPricePoints(tariffsData.tariffs || [])
                    const upgradePricePoints = extractUpgradePricePoints(tariffsData.tariffs || [])
                    
                    // Рассчитываем цену за документ БЕЗ скидки для нового количества
                    const pricePerDocumentOriginal = calculatePricePerDocument(quantity, pricePointsWithoutDiscount)
                    // Рассчитываем цену за документ СО СКИДКОЙ для нового количества
                    const pricePerDocumentDiscounted = calculatePricePerDocument(quantity, pricePointsWithDiscount)
                    
                    // Оригинальная цена (без скидки) в копейках
                    const originalPrice = Math.round(pricePerDocumentOriginal * quantity * 100)
                    // Цена со скидкой в копейках
                    const discountedPrice = Math.round(pricePerDocumentDiscounted * quantity * 100)
                    
                    // Вычисляем скидку в процентах на основе разницы между оригинальной ценой и ценой со скидкой
                    const calculatedDiscount = calculateDiscountPercent(originalPrice, discountedPrice)
                    
                    // Рассчитываем цены апгрейдов для нового количества
                    const upgradePrices = calculateUpgradePrices(quantity, upgradePricePoints)
                    
                    // Преобразуем цены апгрейдов обратно в формат upgrade_additional_price
                    const upgradeAdditionalPrice: { [key: string]: number } = {}
                    Object.entries(upgradePrices).forEach(([upgradeId, upgradeData]) => {
                        // Цена за весь пакет = цена за документ * количество документов
                        upgradeAdditionalPrice[upgradeId] = Math.round(upgradeData.price * quantity * 100)
                    })
                    
                    console.log('upgradeAdditionalPrice', upgradeAdditionalPrice)
                    console.log('originalPrice', originalPrice, 'discountedPrice', discountedPrice, 'calculatedDiscount', calculatedDiscount)
                    // Обновляем тариф с новым количеством, оригинальной ценой и рассчитанной скидкой
                    // В корзине храним оригинальную цену + рассчитанную скидку, скидка применяется при расчете итоговой стоимости
                    const updatedCart = [...prevCart]
                    updatedCart[tariffIndex] = {
                        ...tariff,
                        prepaid_documents_count: quantity,
                        price: originalPrice, // Оригинальная цена без скидки
                        discount: calculatedDiscount, // Рассчитанная скидка на основе разницы цен
                        upgrade_additional_price: upgradeAdditionalPrice,
                        isMinDiscount: false // Количество выбрано, это не минимальная скидка
                    }
                    
                    console.log('Updated custom tariff:', updatedCart[tariffIndex])
                    return updatedCart
                }
            }
            
            return prevCart
        })
    }


    function renderServicesContent() {
        return (
            <>
                <div className="currentTariffSection">
                    <MyTariffV2 
                        currentTariff={tariffsData.current_tariff}
                        proSubscriptionStatus={tariffsData.pro_subscription_status}
                        $store={props.$store}
                        $commitToStore={props.$commitToStore}
                        onLoadTariffs={loadTariffs}
                    />
                </div>

                <div className="tariffsSection">
                    <h2 className="H3_21_v2">Выберите тариф</h2>
                    <div className="tariffs-grid-v2">
                        <TariffCards
                            tariffs={tariffsData.tariffs || []}
                            onAddToCart={addTariffToCart}
                            cart={cart}
                        />
                    </div>
                </div>

                {/* Показываем слайдер только если индивидуальный тариф выбран */}
                {isCustomTariffSelected() && (
                    <div className="customSliderSection" style={{marginTop: '28px'}}>
                            <div className="H3_21_v2">
                                Выберите количество документов:
                            </div>
                            <CustomQuantitySlider
                                tariffs={tariffsData.tariffs || []}
                                onQuantityChange={updateCustomTariffQuantity}
                            />
                    </div>
                )}

                <div className="additionalServicesSection">
                    <AdditionalServices
                        tariffsData={tariffsData}
                        onAddUpgradeToCart={addUpgradeToCart}
                        onAddServiceToCart={addAdditionalServiceToCart}
                        onAddProSubscriptionToCart={addProSubscriptionToCart}
                        cart={cart}
                        $store={props.$store}
                    />
                </div>
            </>
        )
    }

    if (loading) {
        return <PageLoader />
    }

    if (error) {
        return (
            <ErrorPageView 
                error={error}
                $commitToStore={props.$commitToStore}
                customAction={{
                    text: 'Попробовать снова',
                    onClick: loadTariffs
                }}
            />
        )
    }

    return (
        <div className="tariffs-v2-page ui-kit-v2-page-white-bg">
            {/* Горизонтальная полоса для отделения от шапки */}
            <div className="divider-full-width"></div>
            <div className="mainContent">
                <div className="contentWrapper" style={{paddingTop: '10px'}}>
                    <aside className="tariffsAside">
                        {/* Табы */}
                        <TabSelectorV2
                            options={['Тарифы', 'Счета', 'История']}
                            selectedOption={activeTab}
                            onSelectOption={setActiveTab}
                        />

                        {activeTab === 'Тарифы' ? (
                            renderServicesContent()
                        ) : activeTab === 'Счета' ? (
                            <div className="bills-content">
                                <BillsList
                                    $store={props.$store}
                                    $commitToStore={props.$commitToStore}
                                    onLoadTariffs={loadTariffs}
                                />
                            </div>
                        ) : (
                            <div className="history-content">
                                <TransactionHistory
                                    $store={props.$store}
                                    $commitToStore={props.$commitToStore}
                                />
                            </div>
                        )}
                    </aside>

                    {/* Показываем корзину в aside только на десктопе */}
                    {!mobile && (
                        <aside className="cartAside">
                            <Cart
                                cart={cart}
                                onRemoveItem={removeCartItem}
                                onRemovePackageUpgrade={removePackageUpgrade}
                                onUpdateQuantity={updateCartItemQuantity}
                                onClearCart={clearCart}
                                onBillCreated={handleBillCreated}
                                onPaymentProofUploaded={handlePaymentProofUploaded}
                                onPaymentRequest={handlePaymentRequest}
                                onLoadTariffs={loadTariffs}
                                promoCodes={promoCodes}
                                showSbpButton={tariffsData.show_sbp_button === true}
                                $store={props.$store}
                                $commitToStore={props.$commitToStore}
                            />
                        </aside>
                    )}
                </div>
            </div>

            {/* Плавающая кнопка корзины для мобильной версии */}
            <FloatingCartButton
                cart={cart}
                onRemoveItem={removeCartItem}
                onRemovePackageUpgrade={removePackageUpgrade}
                onUpdateQuantity={updateCartItemQuantity}
                onClearCart={clearCart}
                onBillCreated={handleBillCreated}
                onPaymentProofUploaded={handlePaymentProofUploaded}
                onPaymentRequest={handlePaymentRequest}
                onLoadTariffs={loadTariffs}
                promoCodes={promoCodes}
                showSbpButton={tariffsData.show_sbp_button === true}
                $store={props.$store}
                $commitToStore={props.$commitToStore}
            />

            {
                paymentNotification &&
                <PaymentStatus
                    $store={props.$store}
                    $commitToStore={props.$commitToStore}
                    setPaymentNotification={setPaymentNotification}
                    transactionId={$router.route.params.transactionId || new URLSearchParams(window.location.search).get('transactionId')}
                    success={$router.route.params.success || new URLSearchParams(window.location.search).get('success')}
                    onSuccessfulPayment={handleSuccessfulPayment}
                />
            }

            {/* Попап выбора шаблона перед оплатой */}
            {showTemplateConfigPopup && (
                <ConfigureTemplateAfterPaymentPopup
                    mode={getTemplateConfigPopupMode()}
                    onClose={() => {
                        setShowTemplateConfigPopup(false)
                        setPendingPaymentMethod(null)
                    }}
                    onTemplateSelected={handleTemplateSelected}
                    $store={props.$store}
                    $commitToStore={props.$commitToStore}
                />
            )}

            {showBillPopup && (
                <RequestBillPopupV2
                    close={() => setShowBillPopup(false)}
                    onCollectBonus={() => {}}
                    onBillCreated={handleBillCreated}
                    onLoadTariffs={loadTariffs}
                    onPaymentProofUploaded={() => {
                        setShowBillPopup(false)
                        handlePaymentProofUploaded(billItems)
                    }}
                    $store={props.$store}
                    $commitToStore={props.$commitToStore}
                    items={billItems}
                />
            )}

            {subscriptionPopupTariff && (
                <SubscriptionPopup
                    onClose={() => {
                        setSubscriptionPopupTariff(null)
                        setPendingPaymentMethod(null)
                    }}
                    tariff={subscriptionPopupTariff}
                    $store={props.$store}
                    subscribe={handleSubscriptionConfirmed}
                />
            )}
        </div>
    )
}

TariffsV2Page = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({ $commitToStore: (data: any) => dispatch({ ...data, type: 'S' }) })
)(TariffsV2Page)

export default TariffsV2Page
